import { Link } from 'react-router-dom';

import { useSession } from '@/lib/auth';
import { useUserProfile } from '@/hooks/useUserProfile';
import { daysUntilExpiry, lifecycleSeverity } from '@/lib/membershipLifecycle';

/**
 * MembershipLifecycleBanner — banner global de vencimiento de membresía.
 * ROUND12.
 *
 * Reglas:
 *  - Sin sesión o sin perfil → null (no renderea).
 *  - subscription_expires_at < ahora → banner rojo "Tu membresía expiró".
 *  - 0 < days ≤ 7 → banner amarillo "Vence en X días".
 *  - days > 7 o sin suscripción activa → null.
 *
 * Montado en `AppShell` debajo del Header. Patrón compartido con web/app
 * (cross-surface) per la orden ROUND12.
 */
export function MembershipLifecycleBanner(): JSX.Element | null {
  const { user } = useSession();
  const { data: profile } = useUserProfile();

  if (!user || !profile) return null;

  // Si nunca tuvo suscripción ni hay expiry conocido, no hay nada que avisar.
  const expiresAt = profile.subscriptionExpiresAt;
  if (!expiresAt) return null;

  // Si ni siquiera hay rastro de plan asociado, evitamos un banner huérfano.
  if (!profile.subscriptionPlan) return null;

  const days = daysUntilExpiry(expiresAt);
  const sev = lifecycleSeverity(days);

  if (sev === 'ok') return null;

  const planSlug = profile.subscriptionPlan;
  const cycle = profile.subscriptionCycle ?? 'monthly';
  const renewHref = `/memberships/${planSlug}?cycle=${cycle}`;

  if (sev === 'expired') {
    return (
      <div role="alert" className="bg-danger/15 text-fc-ink">
        <div className="container-fc flex flex-wrap items-center justify-between gap-3 py-2 text-sm">
          <span>
            <span aria-hidden>⛔ </span>
            <strong>Tu membresía expiró.</strong> Reactivá tu plan para no perder los beneficios.
          </span>
          <Link
            to={renewHref}
            className="inline-flex items-center rounded-card bg-danger px-3 py-1 text-xs font-medium text-white hover:bg-danger-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-danger"
          >
            Reactivar
          </Link>
        </div>
      </div>
    );
  }

  // 'soon' o 'critical' — ambos usan amarillo (la severidad crítica ya
  // matiza con el "1 día" / "2 días").
  const label = days === 1 ? 'Tu membresía vence mañana.' : `Tu membresía vence en ${days} días.`;
  return (
    <div role="status" className="bg-warning/15 text-fc-ink">
      <div className="container-fc flex flex-wrap items-center justify-between gap-3 py-2 text-sm">
        <span>
          <span aria-hidden>⚠️ </span>
          <strong>{label}</strong> Renová ahora y mantené tus beneficios sin pausa.
        </span>
        <Link
          to={renewHref}
          className="inline-flex items-center rounded-card bg-warning px-3 py-1 text-xs font-medium text-fc-bg hover:bg-warning-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-warning"
        >
          Renovar
        </Link>
      </div>
    </div>
  );
}
